import { Box } from '@mui/material';
import type { AuditLogEntry } from '@core/api/auditLog';
import { AuditAction } from '@core/enum/auditAction';
import type { AuditLogRenderer } from '@core/utils/auditLog';
import { toProperCase } from '@core/utils/string';
import { ORDER_STATUS_PALETTE, OrderStatus } from '../../enum/orderStatus';

type OrderStatusChangedEntry = AuditLogEntry & {
  action: AuditAction.OrderStatusChanged;
  changes: {
    status: { old: OrderStatus | null; new: OrderStatus };
  };
  metadata: {
    itemCount?: number;
    materializedDefaults?: unknown[];
    allItemsQueuedForPrint?: boolean;
  } | null;
};

function renderStatusLabel(status: OrderStatus) {
  const palette =
    ORDER_STATUS_PALETTE[status] ?? ORDER_STATUS_PALETTE[OrderStatus.Draft];

  return (
    <Box component="span" sx={{ color: `${palette}.800` }}>
      {toProperCase(status).replace(/ For /g, ' for ')}
    </Box>
  );
}

export const renderOrderStatusChanged: AuditLogRenderer = (entry) => {
  const { changes, metadata } = entry as OrderStatusChangedEntry;
  const { old: oldStatus, new: status } = changes.status;
  const label = renderStatusLabel(status);

  if (status === OrderStatus.Cancelled || status === OrderStatus.Released) {
    return <>Order {label}</>;
  }

  if (
    status === OrderStatus.InProduction &&
    oldStatus === OrderStatus.ReadyForRelease
  ) {
    return <>Order moved back to {label}</>;
  }

  if (
    status === OrderStatus.InProduction &&
    oldStatus === OrderStatus.ReadyForProduction &&
    metadata?.allItemsQueuedForPrint
  ) {
    return <>Order set to {label} and queued every item for printing</>;
  }

  if (
    oldStatus === OrderStatus.Cancelled ||
    (oldStatus === OrderStatus.Released &&
      status === OrderStatus.ReadyForRelease)
  ) {
    return <>Order restored to {label}</>;
  }

  return <>Order set to {label}</>;
};
